<template>
    <b-container id="project-assignment-console" fluid>
        <b-row>
            <b-col cols="12">
                <div class="border-bottom mb-3 pb-3 d-flex align-items-end justify-content-between flex-wrap">
                    <div>
                        <h3 class="mb-1">프로젝트 배정 콘솔</h3>
                        <p class="fw-400 text-secondary mb-0">
                            프로젝트를 고르면 신청 질문과 배정 규칙을 한 화면에서 볼 수 있습니다.
                        </p>
                    </div>
                    <div class="d-flex align-items-center gap-2 mt-2">
                        <b-button
                            size="sm"
                            variant="outline-primary"
                            :disabled="!selectedProjectId || loading || !!loadError"
                            @click="openPreview"
                        >
                            <i class="bi bi-play-circle mr-1"></i>배정 미리보기
                        </b-button>
                        <b-dropdown size="sm" variant="outline-secondary" right text="고급">
                            <b-dropdown-item @click="goAdvanced('ManagingProjectSchema', 'schemaInput')">
                                신청 양식 · 선택지 (고급)
                            </b-dropdown-item>
                            <b-dropdown-item @click="goAdvanced('ManagingProjectSchema', 'localeString')">
                                언어별 문구 (고급)
                            </b-dropdown-item>
                            <b-dropdown-divider/>
                            <b-dropdown-item @click="goAdvanced('ManagingCampaignConnectRule', 'condition')">
                                답변 조건 (고급)
                            </b-dropdown-item>
                            <b-dropdown-item @click="goAdvanced('ManagingCampaignConnectRule', 'ruleGroup')">
                                조건 묶음 (고급)
                            </b-dropdown-item>
                            <b-dropdown-item @click="goAdvanced('ManagingCampaignConnectRule', 'campaignRule')">
                                배정 규칙 (고급)
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </div>
            </b-col>
        </b-row>

        <!-- 프로젝트 선택 -->
        <b-row class="mb-3">
            <b-col cols="12" md="8" lg="6">
                <label class="fw-500 mb-1">프로젝트</label>
                <auto-complete-select
                    ref="projectSelect"
                    v-model="selectedProjectId"
                    :options="projectCodeOptions"
                    start-expanded
                    clearable
                    placeholder="프로젝트를 선택해주세요."
                />
            </b-col>
        </b-row>

        <template v-if="selectedProjectId">
            <div v-if="loading" class="text-center py-5">
                <b-spinner/>
                <div class="text-secondary mt-2">불러오는 중…</div>
            </div>

            <b-alert v-else-if="loadError" variant="danger" show>
                <i class="bi bi-exclamation-triangle-fill mr-1"></i>{{ loadError }}
                <b-button size="sm" variant="outline-danger" class="ml-2" @click="reload">다시 시도</b-button>
            </b-alert>

            <template v-else>
                <!-- 준비 상태 배너 -->
                <b-row class="mb-3">
                    <b-col cols="12">
                        <readiness-banner :readiness="readiness" @cta="onBannerCta"/>
                    </b-col>
                </b-row>

                <!-- ① 신청 질문 -->
                <b-row>
                    <b-col cols="12">
                        <question-section
                            :input-keys="inputKeys"
                            :missing-keys="missingKeys"
                            @add="openQuestionModal('add')"
                            @edit="openQuestionModal('detail', $event)"
                            @reorder="openOrderModal"
                            @create-missing="openQuestionModal('add')"
                        />
                    </b-col>
                </b-row>

                <!-- ② 배정 규칙 -->
                <b-row>
                    <b-col cols="12">
                        <assignment-rule-section
                            :rule-sets="ruleSets"
                            :total="ruleSetTotal"
                            :truncated="ruleSetTruncated"
                            :key-map="keyMap"
                            :missing-keys="missingKeys"
                            :fallback-campaign-name="fallbackCampaignName"
                            @add="openRuleBuilder"
                            @edit="openRuleModal('detail', $event)"
                            @preview="openPreview"
                        />
                    </b-col>
                </b-row>
            </template>
        </template>

        <p-page-empty-component v-else text="먼저 프로젝트를 선택해주세요."/>

        <assignment-preview-modal
            :modal-id="previewModalId"
            :project-id="selectedProjectId"
            :input-keys="inputKeys"
            :campaigns="campaigns"
        />

        <!-- 인플레이스 편집: 기존 관리 모달을 그대로 임베드(신규 저장 로직 없음).
             저장/삭제/순서변경 후에는 onDataChanged 로 콘솔 데이터를 다시 불러
             배너·질문·규칙을 갱신한다(입력키 삭제 시 missing 배너 = 안전그물). -->
        <schema-input-modal
            ref="questionModal"
            :detail-id="modalQuestionId"
            :selected-project-code-id="selectedProjectId"
            :projects="projects"
            allow-copy-import
            lock-project-to-selected
            @request-copy-import="openCopyPicker"
            @addData="onDataChanged"
            @updateDetailData="onDataChanged"
            @deleteData="onDataChanged"
        />
        <!-- 다른 스키마에서 복사: 전체 입력 값 목록에서 하나를 골라 질문 모달에 draft 로 시드.
             저장은 유저가 직접(openAsCopy 는 폼만 채운다). -->
        <schema-input-copy-picker-modal
            :projects="projects"
            @select="onCopyImportSelect"
        />
        <schema-input-order-change-modal
            :project-text="selectedProjectText"
            :schema-inputs="inputKeys"
            @schemaInputsOrderChanged="onDataChanged"
        />
        <campaign-rule-set-modal
            ref="ruleModal"
            :detail-id="modalRuleId"
            :campaigns="campaigns"
            :projects="projects"
            @addData="onDataChanged"
            @updateDetailData="onDataChanged"
            @deleteData="onDataChanged"
        />
        <!-- 규칙 추가는 문장 빌더로: 질문 드롭다운을 등록 입력키만으로 채워 미등록 key 참조를 원천 차단.
             저장은 조건→규칙그룹→규칙셋(마지막) 안전순서. -->
        <assignment-rule-sentence-builder
            :modal-id="builderModalId"
            :project-id="selectedProjectId"
            :project-code="selectedProjectCode"
            :input-keys="inputKeys"
            :campaigns="campaigns"
            @created="onDataChanged"
            @create-question="openQuestionModal('add')"
        />
    </b-container>
</template>

<script>
import {mapState} from 'vuex';
import AutoCompleteSelect from '@core/components/common/AutoCompleteSelect.vue';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import {getCampaignReadiness, getProjectSchema} from '@shared_utils/api/projectSchema';
import {listCampaignRuleSets, getCampaignRuleSet} from '@shared_utils/api/ruleEngine';
import {getProjectCodeOptions, getTargetCampaignText, getTargetProjectText} from '@shared/components/admin/pages/Management/common-data';
import {buildKeyMap} from './plain-language';
import ReadinessBanner from './ReadinessBanner.vue';
import QuestionSection from './QuestionSection.vue';
import AssignmentRuleSection from './AssignmentRuleSection.vue';
import AssignmentPreviewModal from './AssignmentPreviewModal.vue';
import AssignmentRuleSentenceBuilder from './AssignmentRuleSentenceBuilder.vue';
import SchemaInputModal from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/SchemaInputModal.vue';
import SchemaInputOrderChangeModal from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/SchemaInputOrderChangeModal.vue';
import SchemaInputCopyPickerModal from './SchemaInputCopyPickerModal.vue';
import CampaignRuleSetModal from '@shared/components/admin/pages/Management/CampaignConnectRule/CampaignRuleSet/CampaignRuleSetModal.vue';

export default {
    name: 'ProjectConsole',
    components: {
        AutoCompleteSelect,
        'p-page-empty-component': PageEmptyComponent,
        ReadinessBanner,
        QuestionSection,
        AssignmentRuleSection,
        AssignmentPreviewModal,
        AssignmentRuleSentenceBuilder,
        SchemaInputModal,
        SchemaInputOrderChangeModal,
        SchemaInputCopyPickerModal,
        CampaignRuleSetModal,
    },
    data() {
        return {
            projects: null,
            campaigns: [],
            selectedProjectId: null,
            loading: false,
            loadError: null,
            readiness: null,
            inputKeys: [],
            ruleSets: [],
            ruleSetTotal: 0,
            ruleSetTruncated: false,
            fallbackCampaignName: null,
            previewModalId: 'assignment-preview-modal',
            builderModalId: 'assignment-rule-builder-modal',
            // 인플레이스 편집 모달에 넘길 대상 id (null = 신규 추가).
            modalQuestionId: null,
            modalRuleId: null,
            // 프로젝트 전환 시 오래된 응답이 뒤늦게 덮어쓰지 않도록 요청 순번을 센다.
            loadToken: 0,
            // 규칙셋 상세 조회 동시성 상한 (프로젝트 전환 시 요청 폭주 방지).
            DETAIL_CONCURRENCY: 6,
            RULE_SET_PAGE_SIZE: 200,
            // 안전 상한: 한 프로젝트에서 가져올 규칙셋 페이지 최대치(무한 루프/폭주 방지).
            RULE_SET_MAX_PAGES: 25,
        };
    },
    computed: {
        ...mapState(['user_id']),
        projectCodeOptions() {
            return getProjectCodeOptions(this.projects);
        },
        keyMap() {
            return buildKeyMap(this.inputKeys);
        },
        missingKeys() {
            return (this.readiness && this.readiness.missing_keys) || [];
        },
        selectedProjectText() {
            return getTargetProjectText(this.selectedProjectId, this.projects);
        },
        // 규칙셋 이름 조립용 프로젝트 코드(짧은 형태). 못 찾으면 null.
        selectedProjectCode() {
            const groups = this.projects || [];
            for (const g of groups) {
                const found = (g.projects || []).find((p) => p.id === this.selectedProjectId);
                if (found) return found.project_code;
            }
            return null;
        },
    },
    watch: {
        selectedProjectId(next) {
            this.syncQuery(next);
            // 선택이 바뀌면(전환·해제 포함) 항상 진행 중 조회를 무효화한다.
            // 늦은 이전-프로젝트 응답이 새 상태를 덮어쓰지 않도록 토큰을 올린다(경쟁상태 게이트).
            const token = ++this.loadToken;
            this.clearProjectState();
            if (next) {
                this.loadProjectData(next, token);
            }
            else {
                this.loading = false;
            }
        },
    },
    async created() {
        await Promise.all([this.loadProjects(), this.loadCampaigns()]);
        // 딥링크: ?project_id= 로 들어오면 해당 프로젝트를 선택 상태로 복원.
        const qid = this.$route.query.project_id;
        if (qid != null && qid !== '') {
            const id = Number(qid);
            if (!Number.isNaN(id)) this.selectedProjectId = id;
        }
    },
    methods: {
        async loadProjects() {
            try {
                const res = await this.$axios.get('/project_code');
                this.projects = res.data;
            }
            catch (e) {
                this.$EventBus.$emit('toast-failure', '오류', '프로젝트 목록을 불러오지 못했습니다.');
            }
        },
        async loadCampaigns() {
            try {
                const res = await this.$axios.get('/read_campaigns_industries', {params: {user_id: this.user_id}});
                this.campaigns = res.data || [];
            }
            catch (e) {
                this.$EventBus.$emit('toast-failure', '오류', '평가지 목록을 불러오지 못했습니다.');
            }
        },
        clearProjectState() {
            this.readiness = null;
            this.inputKeys = [];
            this.ruleSets = [];
            this.ruleSetTotal = 0;
            this.ruleSetTruncated = false;
            this.fallbackCampaignName = null;
            this.loadError = null;
        },
        reload() {
            if (this.selectedProjectId) {
                const token = ++this.loadToken;
                this.loadError = null;
                this.loadProjectData(this.selectedProjectId, token);
            }
        },
        // 늦은 응답 폐기: 이 조회의 token 이 아직 최신이고, 대상 프로젝트가 여전히 선택 중일 때만 반영.
        isStale(token, projectId) {
            return token !== this.loadToken || projectId !== this.selectedProjectId;
        },
        async loadProjectData(projectId, token) {
            this.loading = true;
            this.loadError = null;
            try {
                const [readinessRes, schemaRes, ruleSetList] = await Promise.all([
                    getCampaignReadiness(projectId),
                    getProjectSchema(projectId),
                    this.fetchAllRuleSets(projectId, token),
                ]);

                // 규칙셋 상세(조건 트리)를 채운다. 목록 응답은 root_rule_group 이 얕아
                // 조건 문장을 그리려면 상세가 필요하다. 요청 폭주를 막으려 동시성 상한을 둔다.
                // 상세 실패는 detailError 로 표시(네트워크 오류를 '조건 없음'으로 위장하지 않음).
                const details = await this.fetchDetailsBounded(ruleSetList.items, token);

                // 프로젝트가 그 사이 바뀌거나 해제됐으면(늦은 응답) 버린다.
                if (this.isStale(token, projectId)) return;

                this.readiness = readinessRes.data;
                this.inputKeys = (schemaRes.data && schemaRes.data.input_keys) || [];
                this.ruleSets = details.map((rs) => ({
                    ...rs,
                    campaignName: getTargetCampaignText(rs.campaign_id, this.campaigns),
                }));
                this.ruleSetTotal = ruleSetList.total;
                this.ruleSetTruncated = ruleSetList.truncated;
                const fbId = readinessRes.data && readinessRes.data.fallback_campaign_id;
                // 폴백 ID 가 있으면 이름을 못 찾아도 '연결 안 됨'이 아니라 #id 로 표시(#7).
                this.fallbackCampaignName = fbId != null
                    ? (getTargetCampaignText(fbId, this.campaigns) || `평가지 #${fbId}`)
                    : null;
            }
            catch (e) {
                if (this.isStale(token, projectId)) return;
                this.loadError = '프로젝트 배정 정보를 불러오지 못했습니다.';
            }
            finally {
                if (!this.isStale(token, projectId)) this.loading = false;
            }
        },
        // 규칙셋 목록을 전 페이지에 걸쳐 모두 가져온다("모든 규칙을 한 화면"). 하드 상한을 두고,
        // 상한 초과 시 truncated 로 알린다. 전환/해제 시(token 불일치) 즉시 중단.
        async fetchAllRuleSets(projectId, token) {
            const items = [];
            let page = 1;
            let total = null;
            let truncated = false;
            while (page <= this.RULE_SET_MAX_PAGES) {
                const res = await listCampaignRuleSets(
                    {params: {project_id: projectId, page, per_page: this.RULE_SET_PAGE_SIZE}});
                if (this.isStale(token, projectId)) break;
                const data = res.data || {};
                const pageItems = data.campaign_rule_sets || [];
                if (total == null) total = data.total != null ? data.total : pageItems.length;
                items.push(...pageItems);
                if (pageItems.length < this.RULE_SET_PAGE_SIZE || items.length >= total) break;
                page += 1;
                if (page > this.RULE_SET_MAX_PAGES) truncated = items.length < total;
            }
            return {items, total: total != null ? total : items.length, truncated};
        },
        // 상세 조회를 동시성 상한만큼씩 나눠 순차 실행. 실패 항목은 얕은 목록 + detailError.
        // token 이 낡으면(프로젝트 전환/해제) worker 가 다음 항목을 꺼내기 전에 즉시 멈춘다.
        async fetchDetailsBounded(listed, token) {
            const out = new Array(listed.length);
            let cursor = 0;
            const worker = async () => {
                while (cursor < listed.length) {
                    if (token !== this.loadToken) return; // 전환됨 → 남은 상세 요청 발사 중단
                    const idx = cursor++;
                    const rs = listed[idx];
                    try {
                        const d = await getCampaignRuleSet(rs.id);
                        out[idx] = d.data;
                    }
                    catch (e) {
                        out[idx] = {...rs, detailError: true};
                    }
                }
            };
            const n = Math.min(this.DETAIL_CONCURRENCY, listed.length || 1);
            await Promise.all(Array.from({length: n}, () => worker()));
            return out;
        },
        syncQuery(projectId) {
            const current = this.$route.query.project_id;
            const nextVal = projectId != null ? String(projectId) : undefined;
            if (current === nextVal) return;
            this.$router.replace({
                name: 'ManagingProjectConsole',
                query: projectId != null ? {project_id: String(projectId)} : {},
            }).catch(() => {}); // NavigationDuplicated 무시
        },
        openPreview() {
            this.$bvModal.show(this.previewModalId);
        },
        // 신청 질문 추가/편집 — 기존 SchemaInputModal 을 그대로 연다(ManageSchemaInput 과 동일 패턴).
        openQuestionModal(mode, item) {
            this.modalQuestionId = mode === 'add' ? null : (item && item.id);
            this.$refs.questionModal.changeMode(mode);
            this.$bvModal.show('schema-input-detail-modal');
        },
        openOrderModal() {
            this.$bvModal.show('schema-input-order-change-modal');
        },
        // "다른 스키마에서 복사" — 질문 모달(add) 안의 버튼이 요청. picker 목록을 연다.
        openCopyPicker() {
            this.$bvModal.show('schema-input-copy-picker-modal');
        },
        // picker 에서 소스 입력 값을 고르면, 열려있는 질문 모달을 그 값으로 copy-seed 한다.
        // 폼만 채우며(연결 프로젝트는 비움) 저장은 유저가 직접 눌러야 반영된다.
        onCopyImportSelect(sourceId) {
            this.$bvModal.hide('schema-input-copy-picker-modal');
            this.$refs.questionModal.openAsCopy(sourceId);
        },
        // 배정 규칙 추가 — 문장 빌더(등록 질문만 선택 가능).
        openRuleBuilder() {
            this.$bvModal.show(this.builderModalId);
        },
        // 배정 규칙 추가/편집 — 기존 CampaignRuleSetModal 을 그대로 연다(삭제도 모달 내부).
        openRuleModal(mode, item) {
            this.modalRuleId = mode === 'add' ? null : (item && item.id);
            this.$refs.ruleModal.changeMode(mode);
            this.$bvModal.show('campaign-rule-set-detail-modal');
        },
        // 저장/삭제/순서변경 후: 콘솔 데이터를 다시 불러 배너·질문·규칙을 서버 기준으로 갱신.
        onDataChanged() {
            this.reload();
        },
        onBannerCta({action}) {
            if (action === 'create-question') {
                this.openQuestionModal('add');
            }
            else if (action === 'connect-fallback') {
                // 기본 평가지(project.campaign)는 프로젝트 코드 관리에서 설정한다.
                this.$router.push({name: 'ManagingProjectCode'}).catch(() => {});
            }
        },
        goAdvanced(routeName, tab) {
            this.$router.push({name: routeName, query: {tab}}).catch(() => {});
        },
    },
};
</script>

<style lang="scss" scoped>
.gap-2 {
    gap: 0.5rem;
}
</style>
